import { motion } from "framer-motion";
import { ArrowLeft, Clock, Bell, Sparkles, Phone } from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO = "/logo-transparent.png";
const QUOTE_URL = "https://tradehq.com.au/albanyfencingcontractors/enquire";
const PHONE_NUMBER = "0438 185 148";

const ProductsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0c1829] via-[#162844] to-[#0c1829] relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Floating particles */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: i % 2 === 0 ? 'rgba(16, 185, 129, 0.4)' : 'rgba(6, 182, 212, 0.4)',
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}

        {/* Animated fence at bottom */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-around px-8">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="w-3 rounded-t-sm"
              style={{
                background: 'linear-gradient(to top, rgba(16, 185, 129, 0.2), rgba(6, 182, 212, 0.05))',
              }}
              initial={{ height: 0 }}
              animate={{ height: `${60 + (i % 4) * 25}px` }}
              transition={{ duration: 0.8, delay: i * 0.05 }}
            />
          ))}
        </div>
        <motion.div
          className="absolute left-0 right-0 h-2"
          style={{ bottom: '50px', background: 'linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.15), transparent)' }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        />
        <motion.div
          className="absolute left-0 right-0 h-2"
          style={{ bottom: '90px', background: 'linear-gradient(90deg, transparent, rgba(6, 182, 212, 0.12), transparent)' }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 sm:gap-3">
            <img src={LOGO} alt="Albany Fencing" className="h-10 sm:h-14 w-auto drop-shadow-lg" />
            <div>
              <span className="font-black text-white text-xs sm:text-base tracking-tight block leading-tight">ALBANY FENCING</span>
              <span className="text-cyan-400 text-[10px] sm:text-xs font-bold uppercase tracking-widest">Contractors</span>
            </div>
          </a>
          
          <a href="/">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-4 sm:px-6 py-2 flex items-center gap-2">
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">Back to Home</span>
            </Button>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex flex-col items-center justify-center min-h-[70vh] px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Icon */}
          <motion.div
            className="mb-8 inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-white/10"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Clock className="w-12 h-12 text-cyan-400" />
          </motion.div>

          {/* Text */}
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white mb-4">
            Coming <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Soon</span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-white/60 mb-8 max-w-2xl">
            Our products catalog is currently under construction.
            <br className="hidden sm:block" />
            Stay tuned for exciting fencing products!
          </p>

          {/* Features coming */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {["Colorbond Panels", "Aluminium Slats", "Gates", "Accessories"].map((item, i) => (
              <motion.span
                key={item}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white/70 text-sm font-medium"
              >
                {item}
              </motion.span>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`}>
              <Button className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white rounded-full px-8 py-5 font-bold flex items-center gap-3 shadow-lg shadow-emerald-500/25">
                <Phone size={20} />
                Call Us: {PHONE_NUMBER}
              </Button>
            </a>
            <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-5 font-bold flex items-center gap-2">
                <Sparkles size={18} />
                Get a Quote Instead
              </Button>
            </a>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer hint */}
      <footer className="relative z-10 text-center pb-8">
        <p className="text-white/40 text-sm">
          © {new Date().getFullYear()} Albany Fencing Contractors
        </p>
      </footer>
    </div>
  );
};

export default ProductsPage;
